import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useArchiveDocumentListByClientId,
  useGetContractClientDocument,
  useGetDocumentListByClientId,
  useGetOtherClientDocument,
} from "../../../../api/services/client/clientApis";

import {
  AccordionHeader,
  AccordionHeaderText,
  WarningConfirmationModal,
} from "../../../../components";
import {
  CLIENT_DOCUMENT_TYPES,
  DOCUMENT_SITE_CORE_KEY,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { isEmpty, isNotEmpty } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  setIsDataModifiedCurrentPage,
  toggleLoader,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";

import {
  DocumentDetailsContract,
  DocumentDetailsOthers,
} from "../../../../api/services/client/models";
import { DocumentType } from "../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import {
  CreateClientDocuments,
  DOCUMENT_TABS_INDEX,
} from "../createClientDocument/CreateClientDocuments";
import { InheritDocuments } from "../inheritDocuments/InheritDocuments";
import { UploadDocumentModal } from "../uploadDocument/UploadDocumentModal";
import ViewDocument from "../viewDocument/ViewDocument";
import { ClientDocumentMainWrapper } from "./ClientDocumentMain.style";

export const pageId = PAGE_IDS.CREATE_CLIENT_CONTACT_RESULT;
export interface ClientDocumentMainProps {
  parentAccordionStatus: boolean;
  onAccordionChange: (expanded: boolean) => void;
  isAddButtonVisible?: boolean;
  isEditable?: boolean;
}

export const ClientDocumentMain = forwardRef(
  (props: ClientDocumentMainProps, ref) => {
    const {
      parentAccordionStatus,
      onAccordionChange,
      isAddButtonVisible = true,
      isEditable = true,
    } = props;
    const { fetchLabel, commonLabels, fetchComponentTitle } = useSiteCore();

    const [documentAccordionStatus, setDocumentAccordionStatus] = useState(
      parentAccordionStatus,
    );
    const [selectedDocumentInfo, setSelectedDocumentInfo] = useState<
      DocumentDetailsOthers | DocumentDetailsContract | null
    >(null);
    const [showMaximumUploadLimitModal, setShowMaximumUploadLimitModal] =
      useState(false);
    const [showUploadDocumentModal, setShowUploadDocumentModal] =
      useState(false);
    const [showViewDocumentModal, setShowViewDocumentModal] = useState(false);
    const [showInheritDocumentsModal, setShowInheritDocumentsModal] =
      useState(false);
    const [viewDocumentData, setViewDocumentData] = useState<
      DocumentDetailsOthers | DocumentDetailsContract
    >();
    const isEditDocumentRef = useRef<boolean | undefined>(false);
    const isViewDocumentRef = useRef<boolean | undefined>(false);

    const [selectDocumentTab, setSelectDocumentTab] = useState(0);
    const DOCUMENT_MAX_UPLOAD_LIMIT = 10;

    const dispatch = useDispatch();
    const { id: clientId } = useSelector(createClientSelector);

    const {
      mutateAsync: getOtherDocuments,
      data: otherDocuments,
      isPending: isOtherDocumentsPending,
      isSuccess: isOtherDocumentsSuccess,
    } = useGetDocumentListByClientId();

    const {
      mutateAsync: getArchiveDocuments,
      data: archiveDocuments,
      isPending: isArchiveDocumentsPending,
      isSuccess: isArchiveDocumentsSuccess,
    } = useArchiveDocumentListByClientId();

    const {
      mutateAsync: getContractDocuments,
      data: contractDocuments,
      isPending: isContractDocumentsPending,
      isSuccess: isContractDocumentsSuccess,
    } = useGetDocumentListByClientId();

    const {
      mutateAsync: getOtherClientDocument,
      data: otherClientDocumentData,
      isSuccess: isGetOtherClientDocumentSuccess,
    } = useGetOtherClientDocument();

    const {
      mutateAsync: getContractClientDocument,
      data: contractClientDocumentData,
      isSuccess: isGetContractClientDocumentSuccess,
    } = useGetContractClientDocument();

    const fetchContractDocuments = () => {
      getContractDocuments({
        clientId,
        documentType: "contract",
      });
    };
    const fetchOtherDocuments = () => {
      getOtherDocuments({
        clientId,
        documentType: "others",
      });
    };

    const fetchAllDocuments = () => {
      fetchContractDocuments();
      fetchOtherDocuments();
      getArchiveDocuments({ clientId });
    };

    useEffect(() => {
      if (!showUploadDocumentModal && isEditDocumentRef.current) {
        isEditDocumentRef.current = false;
        setSelectedDocumentInfo(null);
      }
    }, [showUploadDocumentModal]);

    useEffect(() => {
      if (isEmpty(clientId)) return;
      fetchAllDocuments();
    }, [clientId]);

    useEffect(() => {
      setDocumentAccordionStatus(parentAccordionStatus);
    }, [parentAccordionStatus]);

    useEffect(() => {
      dispatch(
        toggleLoader({
          isLoadingDocuments: {
            isVisible:
              isOtherDocumentsPending ||
              isContractDocumentsPending ||
              isArchiveDocumentsPending,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
      return () => {
        toggleLoader({
          isLoadingDocuments: {
            isVisible: false,
          },
        });
      };
    }, [
      isOtherDocumentsPending,
      isContractDocumentsPending,
      isArchiveDocumentsPending,
    ]);

    useEffect(() => {
      if (
        isOtherDocumentsSuccess ||
        isContractDocumentsSuccess ||
        isArchiveDocumentsSuccess
      ) {
        if (
          !parentAccordionStatus &&
          (isNotEmpty(otherDocuments?.data) ||
            isNotEmpty(contractDocuments?.data)) &&
          isAddButtonVisible
        ) {
          onAccordionChange(true);
        }
      }
    }, [
      isOtherDocumentsSuccess,
      isContractDocumentsSuccess,
      isArchiveDocumentsSuccess,
    ]);

    useEffect(() => {
      if (isGetOtherClientDocumentSuccess) {
        setSelectedDocumentInfo({
          ...otherClientDocumentData,
          documentType: DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
        });
        if (isViewDocumentRef.current) {
          setShowViewDocumentModal(true);
        } else {
          isEditDocumentRef.current = true;
          setShowUploadDocumentModal(true);
        }
      }
    }, [isGetOtherClientDocumentSuccess]);

    useEffect(() => {
      if (isGetContractClientDocumentSuccess) {
        setSelectedDocumentInfo({
          ...contractClientDocumentData,
          documentType: DOCUMENT_SITE_CORE_KEY.documentTypeContract,
        });
        if (isViewDocumentRef.current) {
          setShowViewDocumentModal(true);
        } else {
          isEditDocumentRef.current = true;
          setShowUploadDocumentModal(true);
        }
      }
    }, [isGetContractClientDocumentSuccess]);

    useImperativeHandle(ref, () => ({
      fetchAllDocuments,
    }));

    const handleInheritDocumentClick = () => {
      setShowInheritDocumentsModal(true);
    };

    const handleDocumentUploaded = (
      documentType?: DocumentType,
      isAllowToNavigate = true,
    ) => {
      if (isAllowToNavigate) {
        if (documentType === CLIENT_DOCUMENT_TYPES.clientContract) {
          setSelectDocumentTab(DOCUMENT_TABS_INDEX.CONTRACT);
        } else {
          setSelectDocumentTab(DOCUMENT_TABS_INDEX.OTHERS);
        }
      }
      dispatch(setIsDataModifiedCurrentPage(true));
      fetchAllDocuments();
    };

    const handleDocumentUploadClick = (
      event?: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event?.stopPropagation();
      event?.preventDefault();
      if (selectDocumentTab === DOCUMENT_TABS_INDEX.CONTRACT) {
        if (contractDocuments?.data?.length >= DOCUMENT_MAX_UPLOAD_LIMIT) {
          handleShowMaximumUploadLimit();
          return;
        }
      } else if (otherDocuments?.data?.length >= DOCUMENT_MAX_UPLOAD_LIMIT) {
        handleShowMaximumUploadLimit();
        return;
      }
      setShowUploadDocumentModal(true);
    };

    const handleShowMaximumUploadLimit = () => {
      setShowMaximumUploadLimitModal(true);
    };

    const handleShowMaximumUploadLimitClose = () => {
      if (showMaximumUploadLimitModal) {
        setShowMaximumUploadLimitModal(false);
      }
    };

    const handleCreateClientDocumentChange = (
      _event: React.SyntheticEvent,
      expanded: boolean,
    ) => {
      setDocumentAccordionStatus(expanded);
      onAccordionChange(expanded);
    };

    const handleEditDocument = (documentId: string, documentType: string) => {
      isViewDocumentRef.current = false;
      isEditDocumentRef.current = true;
      setShowViewDocumentModal(false);
      if (documentType === DOCUMENT_SITE_CORE_KEY.documentTypeOthers) {
        getOtherClientDocument(documentId);
      }
      if (documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract) {
        getContractClientDocument(documentId);
      }
    };

    const handleUploadModalClose = () => {
      isViewDocumentRef.current = undefined;
      isEditDocumentRef.current = undefined;
      setShowUploadDocumentModal(false);
    };

    return (
      <ClientDocumentMainWrapper>
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(documentSiteCoreKeys.documentsTitle)}
            />
          }
          onChange={handleCreateClientDocumentChange}
          isExpanded={documentAccordionStatus}
        >
          <CreateClientDocuments
            otherDocuments={otherDocuments?.data}
            contractDocuments={contractDocuments?.data}
            archiveDocuments={archiveDocuments?.data}
            onDocumentEdit={handleEditDocument}
            onDocumentView={(data) => {
              setViewDocumentData(data);
              setShowViewDocumentModal(true);
            }}
            onDocumentUploaded={handleDocumentUploaded}
            selectDocumentTab={selectDocumentTab}
            onDocumentTabChange={(selectedTab) => {
              setSelectDocumentTab(selectedTab);
            }}
            isEditable={isAddButtonVisible}
            onUploadDocumentClick={handleDocumentUploadClick}
            onInheritDocumentClick={handleInheritDocumentClick}
          />
        </AccordionHeader>
        {showMaximumUploadLimitModal && (
          <WarningConfirmationModal
            labelContent={fetchLabel(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.alertUploadDocumentLimit,
            )}
            modalTitle={fetchLabel(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.information,
            )}
            show={showMaximumUploadLimitModal}
            onClose={handleShowMaximumUploadLimitClose}
            onPrimaryButtonClick={handleShowMaximumUploadLimitClose}
            primaryButtonTitle={commonLabels.ok}
            showSecondaryButton={false}
          />
        )}
        {showUploadDocumentModal && (
          <UploadDocumentModal
            open={showUploadDocumentModal}
            data={selectedDocumentInfo ?? undefined}
            isInEditMode={isEditDocumentRef.current}
            onDocumentUploaded={handleDocumentUploaded}
            onClose={() => handleUploadModalClose()}
            selectedTab={selectDocumentTab}
          />
        )}
        {showViewDocumentModal && (
          <ViewDocument
            open={showViewDocumentModal}
            data={viewDocumentData ?? ({} as DocumentDetailsOthers)}
            onClose={() => setShowViewDocumentModal(false)}
            onDocumentEdit={handleEditDocument}
            isEditable={isEditable}
            selectedTab={selectDocumentTab}
          />
        )}
        {showInheritDocumentsModal && (
          <InheritDocuments
            open={showInheritDocumentsModal}
            onClose={() => {
              setShowInheritDocumentsModal(false);
            }}
            onDocumentsInherited={fetchAllDocuments}
          />
        )}
      </ClientDocumentMainWrapper>
    );
  },
);
